import { useSyncExternalStore } from "react";

/** Light, dark, or whatever the phone or computer is set to. */
export type ThemeMode = "system" | "light" | "dark";

const KEY = "zeeta.theme";
const media = () => window.matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<() => void>();

function read(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

let mode: ThemeMode = typeof window === "undefined" ? "system" : read();

/** Puts the class on <html> (public/theme.js does the same before the first paint). */
function apply() {
  const dark = mode === "dark" || (mode === "system" && media().matches);
  const root = document.documentElement;
  root.classList.toggle("dark", dark);
  root.classList.toggle("light", !dark);
  // The phone's status bar and browser bar follow along.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", dark ? "#16181d" : "#0f766e");
}

export function setTheme(next: ThemeMode) {
  mode = next;
  try {
    if (next === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    // Private browsing: the choice lasts until the tab closes.
  }
  apply();
  for (const l of listeners) l();
}

/** Follows the device setting while on "system", and other tabs changing the choice. */
export function installTheme() {
  apply();
  media().addEventListener("change", () => {
    if (mode === "system") {
      apply();
      for (const l of listeners) l();
    }
  });
  window.addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    mode = read();
    apply();
    for (const l of listeners) l();
  });
}

export function useTheme(): { mode: ThemeMode; dark: boolean; setTheme: typeof setTheme } {
  const current = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => mode,
    () => "system" as ThemeMode,
  );
  return {
    mode: current,
    dark: current === "dark" || (current === "system" && media().matches),
    setTheme,
  };
}
